
// src/components/FeedbackForm.tsx
import React, { useState } from 'react';
import { Send, ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface FeedbackFormProps {
  storeId: string;
  onClose: () => void;
}

interface FeedbackData {
  name: string;
  gsm: string;
  email: string;
  message: string;
  level: 'happy' | 'unhappy';
  qrCode: string;
  subject: string;
  storeId: string;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ storeId, onClose }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FeedbackData>({
    name: '',
    gsm: '',
    email: '',
    message: '',
    level: 'happy',
    qrCode: '',
    subject: 'Geri Bildirim',
    storeId: storeId
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFeedbackLevel = (level: 'happy' | 'unhappy') => {
    setFormData(prev => ({ ...prev, level }));
  };
  
  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setErrorMessage(t('feedback.nameRequired') || 'Name is required');
      return false;
    }
    
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      setErrorMessage(t('feedback.validEmailRequired') || 'Valid email is required');
      return false;
    }
    
    if (!formData.message.trim()) {
      setErrorMessage(t('feedback.messageRequired') || 'Message is required');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const response = await fetch('https://gandalf.orwi.app/api/helper/saveFeedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Apikey': 'YWxpLTEyMzQ1MTEyNDU2NTQzMg', // Using the same API key from App.tsx
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
      setErrorMessage(t('feedback.submitError') || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            {t('feedback.title') || 'Send Feedback'}
          </h2>
          
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ThumbsUp className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-green-600 mb-2">
                {t('feedback.thankYou') || 'Thank You!'}
              </h3>
              <p className="text-gray-600">
                {t('feedback.successMessage') || 'Your feedback has been submitted successfully.'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="flex justify-center space-x-6 mb-6">
                <button
                  type="button"
                  className={`p-3 rounded-full ${
                    formData.level === 'happy' 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                  onClick={() => handleFeedbackLevel('happy')}
                >
                  <ThumbsUp className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  className={`p-3 rounded-full ${
                    formData.level === 'unhappy' 
                      ? 'bg-red-100 text-red-600' 
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                  onClick={() => handleFeedbackLevel('unhappy')}
                >
                  <ThumbsDown className="w-6 h-6" />
                </button>
              </div>
              
              {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
                  {errorMessage}
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('feedback.name') || 'Name'}*
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('feedback.phone') || 'Phone Number'}
                </label>
                <input
                  type="tel"
                  name="gsm"
                  value={formData.gsm}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('feedback.email') || 'Email'}*
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('feedback.message') || 'Message'}*
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {t('feedback.submit') || 'Submit Feedback'}
              </button>
              
              {submitStatus === 'error' && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">
                  {errorMessage}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;